import { Trans } from "@lingui/macro";
import { find } from "lodash-es";
import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { useInView } from "react-intersection-observer";
import { Check } from "src/icons/Check";

import {
  BrandGateLevelInCustomerPlatform,
  ProductVariation,
  PromotionDisplayType,
  SingleProduct,
  WishlistItemType,
} from "src/types/graphql.d";
import { ToggleWishListButton } from "src/molecules/ToggleWishListButton";
import { useToggleWishlistProductItem } from "src/hooks/toggleWishlistItem";
import { useAddToWishList } from "src/hooks/useAddToWishlist";
import { useDeleteWishlistItem } from "src/hooks/deleteWishlistItem";
import { ProductSizeSelector } from "src/molecules/ProductSizeSelector";
import { useSegment } from "src/hooks/useSegment";
import currencyMap from "src/utils/currencyMap";
import { useTheme, Theme } from "@emotion/react";
import {
  WishListButtonType,
  WishListIconType,
} from "src/molecules/ToggleWishListButton/ToggleWishListButton";
import { SEGMENT_TOGGLE_WISHLIST_TYPE } from "src/segment";
import { trackProductAddedEvent } from "src/tracking/events/product-added";
import { trackProductFavouritedEvent } from "src/tracking/events/product-favourited";
import { useSession } from "next-auth/react";
import { ProductOrderCartButton } from "src/molecules/ProductOrderCartButton";
import {
  MobileAddToCartFixed,
  SelectSizeError,
  StyledAddingToCartButton,
  StyledProductOrder,
} from "./ProductOrder.styled";
import { getSumStockQuantity, sanitizeString } from "./utils";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { useMiscellaneous } from "src/hooks/useMiscellaneous";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import NoSSR from "src/atoms/NoSSR";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { popPostLoginAddedProduct } from "src/utils/postLoginCartAdded";
import {
  GATED_ADD_PRODUCT_TO_FAVOURITE_SOURCE,
  GATED_ADD_TO_PRODUCT_SOURCE,
} from "src/constants/gatedItem";
import { locale } from "src/config/app-config";
import { SmallSizeGuideHeader } from "../SmallSizeGuideHeader";
import { useCartData, useCartInfo } from "src/hooks/stores/use-cart-store";
import { getConversionItemAttributes } from "src/utils/constructor-beacon";

interface Props {
  product: SingleProduct;
  hasAddToWishlistButton: boolean;
  addToCartLoading: boolean;
}

interface ProductOrderProps extends Props {
  className?: string;
  hasSizeGuide: boolean;
  onAddToCart: (variationId: string) => Promise<boolean>;
  handleSizeChange: (size: ProductVariation) => void;
  onOpenSizeGuide?: () => void;
  openLoginRegisterModal?: (actionSource: string, variationId?: string) => void;
  shippingAndDeliveryInfo?: boolean;
  sizeGuideRef?: RefObject<HTMLDivElement>;
  resultId?: string;
  withMobileOverlay?: boolean;
  hasFlashSaleEnded?: boolean;
  customerType?: "existing" | "new" | "guest";
  selectedSizeSchema?: string;
  onSchemaChange?: (schema: string) => void;
}

interface CallToActionWidgetProps extends Props {
  addToCartLoading: boolean;
  handleAddToCart: () => void;
  isOutOfStock: boolean;
  loading: boolean;
  addedToWishlist: boolean;
  onAddToWishList: (type: WishlistItemType, id: string) => void;
  onRemoveFromWishList: (type: WishlistItemType, id: string) => void;
  isDisabled: boolean;
  hideWishlistButton: boolean;
}

const CallToActionWidget: FC<CallToActionWidgetProps> = ({
  addToCartLoading,
  isOutOfStock,
  handleAddToCart,
  hasAddToWishlistButton,
  product,
  loading,
  addedToWishlist,
  onAddToWishList,
  onRemoveFromWishList,
  isDisabled,
  hideWishlistButton,
}) => {
  return (
    <>
      {hasAddToWishlistButton && !isOutOfStock && !hideWishlistButton && (
        <Box mr={2}>
          <ToggleWishListButton
            dataTestId="add-to-wishlist-button"
            isAddedToWishList={addedToWishlist}
            itemType={WishlistItemType.Product}
            itemId={product.id}
            loading={loading}
            onAddToWishList={onAddToWishList}
            onRemoveWishList={onRemoveFromWishList}
            iconType={WishListIconType.HEART_PAGE}
            buttonType={WishListButtonType.OUTLINED_ICON}
          />
        </Box>
      )}
      {!addToCartLoading ? (
        <ProductOrderCartButton
          isOutOfStock={isOutOfStock}
          onClick={handleAddToCart}
          isDisabled={isDisabled}
        />
      ) : (
        <StyledAddingToCartButton variant="primary" disabled>
          <Trans>Adding to bag</Trans>
        </StyledAddingToCartButton>
      )}
    </>
  );
};

const ProductOrder: FC<ProductOrderProps> = ({
  className,
  product,
  onAddToCart,
  addToCartLoading,
  onOpenSizeGuide,
  hasSizeGuide,
  hasAddToWishlistButton,
  handleSizeChange,
  openLoginRegisterModal,
  shippingAndDeliveryInfo = true,
  sizeGuideRef,
  resultId,
  withMobileOverlay = false,
  customerType,
  selectedSizeSchema: externalSizeSchema,
  onSchemaChange: externalOnSchemaChange,
}) => {
  const theme: Theme = useTheme();
  const { data: miscellaneousData } = useMiscellaneous();
  const isLoggedIn = useIsLoggedIn();
  const { promotionContextState } = usePromotionContext();
  const { cart } = useCartData();

  const currency = currencyMap[locale] || "EUR";
  const [defaultValue, setDefaultValue] = useState<string | undefined>(
    undefined
  );
  const [selectSizeError, setSelectSizeError] = useState(false);
  const [selectedVariationId, setSelectedVariationId] = useState<string>();
  const [internalSizeSchema, setInternalSizeSchema] = useState<
    string | undefined
  >();
  const selectedSizeSchema = externalSizeSchema ?? internalSizeSchema;
  const setSelectedSizeSchema = externalOnSchemaChange ?? setInternalSizeSchema;
  const { enableGatedHomepage } = useFeatureFlags();

  const rawShipping = miscellaneousData?.miscellaneous.pdp.shipping;
  const shippingText = rawShipping ? sanitizeString(rawShipping) : undefined;

  const { segmentProductSizeSelectorClicked } = useSegment();
  const { data: session } = useSession();
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);

  const { isMobile } = useBreakpoints();

  const { ref: selectSizeRef, inView: selectSizeInView } = useInView({
    skip: !isMobile,
    rootMargin: "-60px 0px",
  });

  const { ref: addToCartRef, inView: addToCartInView } = useInView({
    skip: !isMobile,
    rootMargin: "-60px 0px -16px",
    initialInView: true,
  });

  const selectedVariation = selectedVariationId
    ? find(product.variations, ({ id }) => selectedVariationId === id)
    : undefined;

  const { addedToWishlist } = useToggleWishlistProductItem(product.id);
  const { addToWishlist, loading: addLoading } = useAddToWishList({
    refetchQueries: [
      "productBySlug",
      "userPreferences",
      "userWishlistMetaData",
    ],
  });

  const { deleteWishListItem, loading: deleteLoading } = useDeleteWishlistItem({
    refetchQueries: ["productBySlug", "userWishlistMetaData"],
  });

  const onAddToWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      if (isLoggedIn) {
        await addToWishlist(itemType, itemId);
      } else if (enableGatedHomepage && openLoginRegisterModal) {
        openLoginRegisterModal(GATED_ADD_PRODUCT_TO_FAVOURITE_SOURCE);
      }

      void trackProductFavouritedEvent({
        product,
        currency,
        eventType: isLoggedIn
          ? SEGMENT_TOGGLE_WISHLIST_TYPE.favourited
          : SEGMENT_TOGGLE_WISHLIST_TYPE.favouritedWhenLoggedOut,
        email: session?.user?.email,
        resultId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      addToWishlist,
      currency,
      isLoggedIn,
      product,
      session?.user?.email,
      resultId,
    ]
  );

  const onRemoveFromWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await deleteWishListItem(itemType, itemId, {});

      void trackProductFavouritedEvent({
        product,
        currency,
        eventType: SEGMENT_TOGGLE_WISHLIST_TYPE.unFavourited,
        email: session?.user?.email,
        resultId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteWishListItem]
  );

  const handleSelectVariation = useCallback(
    (variationId: string) => {
      setSelectSizeError(false);
      setSelectedVariationId(variationId);

      const variation = product.variations?.find((v) => v.id === variationId);
      if (variation) {
        handleSizeChange(variation);
      }
    },
    [product.variations, handleSizeChange]
  );

  const totalQuantity = getSumStockQuantity(
    product.variations as ProductVariation[]
  );

  const quantity = selectedVariation
    ? selectedVariation.stock_quantity
    : totalQuantity;

  const isOutOfStock = quantity === 0;

  const handleSizeWasNotSelected = useCallback(() => {
    if (isMobile && !selectSizeInView) {
      window.scrollTo({
        behavior: "smooth",
        top: 0,
      });
    }
  }, [selectSizeInView]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBrandGated =
    !!enableGatedHomepage &&
    !isLoggedIn &&
    product?.gate?.level !== BrandGateLevelInCustomerPlatform.NonGated;

  const handleAddToCart = useCallback(
    async (selectedVariationId: string) => {
      const success = await onAddToCart(selectedVariationId);

      if (success && cart) {
        const productCount = cart.lineItems.reduce((count, item) => {
          return count + ((item.quantity as number) || 0);
        }, 0);

        void trackProductAddedEvent({
          product,
          size: selectedVariation?.size as string,
          variationId: selectedVariationId,
          cartData: {
            product_count: productCount,
            cart_total: formatStandardPrice(cart.totalPrice),
          },
          lineItems: cart.lineItems,
          email: session?.user?.email,
          resultId,
        });
      }
      return success;
    },
    [
      onAddToCart,
      product,
      selectedVariation?.size,
      cart,
      session?.user?.email,
      resultId,
    ]
  );

  const onAddToCartClick = useCallback(async (): Promise<boolean> => {
    if (!selectedVariationId || !selectedVariation) {
      handleSizeWasNotSelected();
      setSelectSizeError(true);
      return false;
    }

    if (isBrandGated && openLoginRegisterModal) {
      openLoginRegisterModal(GATED_ADD_TO_PRODUCT_SOURCE, selectedVariationId);
      return false;
    }

    return await handleAddToCart(selectedVariationId);
  }, [
    selectedVariationId,
    selectedVariation,
    isBrandGated,
    openLoginRegisterModal,
    handleAddToCart,
    handleSizeWasNotSelected,
  ]);

  const handleSizeSelectorOpen = useCallback(() => {
    if (!dropDownOpened) {
      setDropDownOpened(true);
      void segmentProductSizeSelectorClicked(product, resultId);
    }
  }, [dropDownOpened, segmentProductSizeSelectorClicked, product, resultId]);

  const isAsicsShoes = () => {
    const brandName = product.brand.name.toLowerCase() === "asics";
    const asicsCategories = [14119, 14471, 21282, 21314];
    const isShoes =
      !!product.categories &&
      product.categories.filter(
        (value) => -1 !== asicsCategories.indexOf(value as number)
      ).length > 0;

    return brandName && isShoes && !isOutOfStock;
  };

  const { id: cartContextId } = useCartInfo();

  useEffect(() => {
    if (
      isLoggedIn &&
      ((customerType === "existing" && cartContextId) || customerType === "new")
    ) {
      const addToCartData = popPostLoginAddedProduct();

      if (addToCartData?.productId === product.id) {
        handleAddToCart(addToCartData.variationId);
      }
    }
  }, [handleAddToCart, isLoggedIn, product.id, customerType, cartContextId]);

  // if there is only one size preselect that size
  // for products that have one size like helmets sunglasses
  // and for products that have multiple sizes but only one single size available
  useEffect(() => {
    const variants = (product.variations as ProductVariation[]).filter(
      (variation) => variation.stock_quantity
    );
    if (variants.length === 1) {
      const firstVariation = variants[0]?.id;
      if (firstVariation) {
        setSelectedVariationId(firstVariation);
        handleSizeChange(variants[0]);
      }
      setDefaultValue(firstVariation);
    }
  }, [product, product.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFlashSale =
    promotionContextState.promotionDisplayType ===
    PromotionDisplayType.FlashSale;

  const hasFlashSaledEnded =
    (promotionContextState.hasPromotionEnded && isFlashSale) || false;

  const { brandSizeFitSmallList = [], categoriesSizeFitSmallList = [] } =
    miscellaneousData?.miscellaneous?.pdp || {};
  const {
    brand: { slug: brandSlug },
    translated_category_slugs: categorySlugs,
  } = product;

  const isSizeFitsSmall = useMemo(() => {
    return (
      brandSizeFitSmallList?.includes(brandSlug) &&
      categorySlugs?.some((category) =>
        categoriesSizeFitSmallList?.includes(category)
      )
    );
  }, [
    brandSlug,
    brandSizeFitSmallList,
    categorySlugs,
    categoriesSizeFitSmallList,
  ]);

  const dropDownHeader = (
    <SmallSizeGuideHeader m={3} onOpenSizeGuide={onOpenSizeGuide} />
  );

  return (
    <StyledProductOrder className={className}>
      {isOutOfStock && (
        <Text
          data-testid="product-quantity-label"
          sx={{
            fontSize: 14,
            lineHeight: "20px",
            color: "tone.anthraciteLight",
          }}
        >
          <Trans>This item is currently out of stock.</Trans>
        </Text>
      )}
      {isAsicsShoes() && (
        <Text
          fontSize={[0]}
          color="tone.anthraciteLight"
          data-testid="hacks-asics-shoes"
        >
          <Trans>
            This items fits smaller. If in doubt, choose one size larger.
          </Trans>
        </Text>
      )}
      <NoSSR>
        {hasSizeGuide && !isMobile && (
          <Text
            fontSize={14}
            textDecoration={"underline"}
            onClick={onOpenSizeGuide}
            sx={{
              marginTop: "auto",
              marginLeft: "auto",
              width: "fit-content",
              cursor: "pointer",
            }}
            data-testid="product-size-guide-button"
          >
            <Trans>Size guide</Trans>
          </Text>
        )}
      </NoSSR>
      {!isOutOfStock && (
        <NoSSR>
          <Flex mt={2} flexDirection="column" ref={selectSizeRef}>
            <ProductSizeSelector
              data-testid="product-order-select-size"
              product={product}
              selectedVariationId={selectedVariationId}
              onSelectVariation={handleSelectVariation}
              onAddToCart={onAddToCartClick}
              addToCartLoading={addToCartLoading}
              hasSizeGuide={hasSizeGuide}
              onOpenSizeGuide={onOpenSizeGuide}
              dropdownHeader={dropDownHeader}
              isSizeFitsSmall={isSizeFitsSmall}
              hasError={selectSizeError}
              disabled={hasFlashSaledEnded}
              defaultValue={defaultValue}
              selectedSchema={selectedSizeSchema}
              onSchemaChange={setSelectedSizeSchema}
              withOverlay={withMobileOverlay}
              overlayExcludeRef={sizeGuideRef}
              ariaDescribedBy="size-error-alert"
              onSizeSelectorOpen={handleSizeSelectorOpen}
              cnstrcItem={getConversionItemAttributes(product)}
            />
          </Flex>
        </NoSSR>
      )}
      <div ref={addToCartRef}>
        {selectSizeError && (
          <SelectSizeError data-testid="product-order-select-size-msg" mt={1}>
            <Trans
              render={({ translation }) => (
                <span role="alert" id="size-error-alert">
                  {translation}
                </span>
              )}
            >
              Please select a size
            </Trans>
          </SelectSizeError>
        )}
        <Flex mt={2} flexWrap="wrap">
          <CallToActionWidget
            handleAddToCart={onAddToCartClick}
            isOutOfStock={isOutOfStock}
            loading={addLoading || deleteLoading}
            addedToWishlist={!!addedToWishlist}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onAddToWishList={onAddToWishList}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onRemoveFromWishList={onRemoveFromWishList}
            product={product}
            hasAddToWishlistButton={hasAddToWishlistButton}
            addToCartLoading={addToCartLoading}
            isDisabled={hasFlashSaledEnded}
            hideWishlistButton={isFlashSale}
          />
        </Flex>
        {isMobile && (
          <NoSSR>
            <MobileAddToCartFixed isVisible={!addToCartInView}>
              {selectSizeError && (
                <SelectSizeError
                  data-testid="product-order-select-size-msg"
                  mt={1}
                >
                  <Trans>Please select a size</Trans>
                </SelectSizeError>
              )}
              <Flex mt={2} flexWrap="wrap">
                <CallToActionWidget
                  handleAddToCart={onAddToCartClick}
                  isOutOfStock={isOutOfStock}
                  loading={addLoading || deleteLoading}
                  addedToWishlist={!!addedToWishlist}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onAddToWishList={onAddToWishList}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onRemoveFromWishList={onRemoveFromWishList}
                  product={product}
                  hasAddToWishlistButton={hasAddToWishlistButton}
                  addToCartLoading={addToCartLoading}
                  isDisabled={hasFlashSaledEnded}
                  hideWishlistButton={isFlashSale}
                />
              </Flex>
            </MobileAddToCartFixed>
          </NoSSR>
        )}
      </div>
      {shippingAndDeliveryInfo && (
        <Flex mt="space24" flexDirection="column">
          {shippingText && (
            <Flex mb={2} sx={{ whiteSpace: "nowrap", alignItems: "center" }}>
              <Check width="24px" height="24px" role="presentation" />
              <Box
                ml="space8"
                fontSize="fontSize14"
                sx={{
                  lineHeight: "20px",
                  color: theme.colors.tone.black,
                }}
              >
                {shippingText}
              </Box>
            </Flex>
          )}
          {miscellaneousData?.miscellaneous.pdp.delivery && (
            <Flex mb={2} sx={{ whiteSpace: "nowrap", alignItems: "center" }}>
              <Check width="24px" height="24px" role="presentation" />
              <Box
                ml="space8"
                fontSize="fontSize14"
                sx={{
                  lineHeight: "20px",
                  color: theme.colors.tone.black,
                }}
                data-testid="free-delivery-message"
              >
                {sanitizeString(miscellaneousData.miscellaneous.pdp.delivery)}
              </Box>
            </Flex>
          )}
          {miscellaneousData?.miscellaneous.pdp.returns && (
            <Flex sx={{ whiteSpace: "nowrap", alignItems: "center" }}>
              <Check width="24px" height="24px" role="presentation" />
              <Box
                ml="space8"
                fontSize="fontSize14"
                sx={{
                  lineHeight: "20px",
                  color: theme.colors.tone.black,
                }}
              >
                {sanitizeString(miscellaneousData.miscellaneous.pdp.returns)}
              </Box>
            </Flex>
          )}
        </Flex>
      )}
    </StyledProductOrder>
  );
};

export default ProductOrder;
